import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { Switch } from '@/components/atoms/switch';
import { TimePicker } from '@/components/molecules/time-picker';
import type { BusinessHours } from '@/types/settings';

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const FULL_DAY_LABELS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];
const DOW_TO_KEY: Record<number, BusinessHours['day']> = {
  0: 'sunday',
  1: 'monday',
  2: 'tuesday',
  3: 'wednesday',
  4: 'thursday',
  5: 'friday',
  6: 'saturday',
};

/** "08:00" → "8:00 AM" */
function fmtTime(t: string): string {
  const [h, m] = t.split(':').map(Number);
  const suffix = h >= 12 ? 'PM' : 'AM';
  const hour = h % 12 === 0 ? 12 : h % 12;
  return `${hour}:${String(m).padStart(2, '0')} ${suffix}`;
}

export interface WeeklyHoursViolation {
  dayOfWeek: number;
  message: string;
}

/**
 * SPEC-scheduling-rules-definitive #2 — validations every weekly-hours surface
 * shares: ranges must be valid (start < end), must not overlap within a day,
 * and — when `businessHours` are provided (technicians) — must fit inside the
 * company's hours (the editor REJECTS, no accept-and-clamp). Pure helper so
 * parents can gate their submit on it.
 */
export function weeklyHoursViolations(
  days: WeeklyHoursDay[],
  businessHours?: BusinessHours[],
): WeeklyHoursViolation[] {
  // Times may arrive as "HH:mm" or "HH:mm:ss" depending on the surface —
  // normalize BOTH sides before comparing, or "17:00:00" > "17:00" false-flags.
  const hhmm = (t: string) => t.slice(0, 5);
  const violations: WeeklyHoursViolation[] = [];
  for (const day of days) {
    if (!day.isAvailable) continue;
    const label = FULL_DAY_LABELS[day.dayOfWeek];
    const ranges = day.timeRanges.map((r) => ({
      startTime: hhmm(r.startTime),
      endTime: hhmm(r.endTime),
    }));
    for (const r of ranges) {
      if (r.startTime >= r.endTime) {
        violations.push({ dayOfWeek: day.dayOfWeek, message: 'Start must be before end.' });
        break;
      }
    }
    const sorted = [...ranges].sort((a, b) => a.startTime.localeCompare(b.startTime));
    for (let i = 1; i < sorted.length; i++) {
      if (sorted[i].startTime < sorted[i - 1].endTime) {
        violations.push({
          dayOfWeek: day.dayOfWeek,
          message: `These hours overlap: ${fmtTime(sorted[i - 1].startTime)} – ${fmtTime(sorted[i - 1].endTime)} and ${fmtTime(sorted[i].startTime)} – ${fmtTime(sorted[i].endTime)}. Merge or adjust the ranges.`,
        });
        break;
      }
    }
    if (businessHours?.length) {
      const bh = businessHours.find((b) => b.day === DOW_TO_KEY[day.dayOfWeek]);
      if (bh && !bh.open) {
        violations.push({
          dayOfWeek: day.dayOfWeek,
          message: `The company is closed on ${label}s — turn the day off or open it in Scheduling settings.`,
        });
      } else if (bh) {
        const open = hhmm(bh.openTime);
        const close = hhmm(bh.closeTime);
        for (const r of ranges) {
          if (r.startTime < open || r.endTime > close) {
            violations.push({
              dayOfWeek: day.dayOfWeek,
              message: `The company is open ${fmtTime(open)} – ${fmtTime(close)} on ${label}s — adjust this shift to fit.`,
            });
            break;
          }
        }
      }
    }
  }
  return violations;
}

export interface WeeklyHoursDay {
  dayOfWeek: number; // 0 = Sun .. 6 = Sat
  isAvailable: boolean;
  timeRanges: { startTime: string; endTime: string }[];
}

/**
 * The weekly working-hours grid (7 days, each toggleable with one or more
 * time ranges). Extracted from TechnicianForm so it can be reused wherever a
 * person's weekly hours are edited — technician-level OR user-level — with the
 * same look and behavior. Controlled: owns no state, edits flow through
 * `onChange`.
 */
export function WeeklyHoursEditor({
  days,
  onChange,
  disabled = false,
  businessHours,
}: {
  days: WeeklyHoursDay[];
  onChange: (days: WeeklyHoursDay[]) => void;
  disabled?: boolean;
  /** Provide to also enforce company hours (technician surfaces). */
  businessHours?: BusinessHours[];
}) {
  const sorted = [...days].sort((a, b) => a.dayOfWeek - b.dayOfWeek);
  const violations = weeklyHoursViolations(days, businessHours);
  const violationFor = (dayOfWeek: number) =>
    violations.find((v) => v.dayOfWeek === dayOfWeek)?.message ?? null;

  function updateDay(dayOfWeek: number, patch: Partial<WeeklyHoursDay>) {
    onChange(days.map((day) => (day.dayOfWeek === dayOfWeek ? { ...day, ...patch } : day)));
  }

  function updateRange(
    dayOfWeek: number,
    index: number,
    patch: { startTime?: string; endTime?: string },
  ) {
    onChange(
      days.map((day) => {
        if (day.dayOfWeek !== dayOfWeek) return day;
        return {
          ...day,
          timeRanges: day.timeRanges.map((range, rangeIndex) =>
            rangeIndex === index ? { ...range, ...patch } : range,
          ),
        };
      }),
    );
  }

  function addRange(dayOfWeek: number) {
    onChange(
      days.map((day) =>
        day.dayOfWeek === dayOfWeek
          ? {
              ...day,
              isAvailable: true,
              timeRanges: [...day.timeRanges, { startTime: '09:00', endTime: '17:00' }],
            }
          : day,
      ),
    );
  }

  function removeRange(dayOfWeek: number, index: number) {
    onChange(
      days.map((day) => {
        if (day.dayOfWeek !== dayOfWeek) return day;
        const nextRanges = day.timeRanges.filter((_range, rangeIndex) => rangeIndex !== index);
        return {
          ...day,
          timeRanges: nextRanges.length ? nextRanges : [{ startTime: '09:00', endTime: '17:00' }],
        };
      }),
    );
  }

  return (
    <div className="space-y-2">
      {sorted.map((day) => (
        <div key={day.dayOfWeek} className="rounded-lg border px-3 py-2">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Switch
                checked={day.isAvailable}
                disabled={disabled}
                onCheckedChange={(checked) =>
                  checked && day.timeRanges.length === 0
                    ? addRange(day.dayOfWeek)
                    : updateDay(day.dayOfWeek, { isAvailable: checked })
                }
              />
              <span className="text-sm font-medium">{WEEKDAY_LABELS[day.dayOfWeek]}</span>
            </div>
            {day.isAvailable ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs text-muted-foreground"
                disabled={disabled}
                onClick={() => addRange(day.dayOfWeek)}
              >
                <Plus className="mr-1 size-3.5" />
                Add range
              </Button>
            ) : null}
          </div>

          {day.isAvailable && violationFor(day.dayOfWeek) ? (
            <p className="mt-2 text-xs text-destructive">{violationFor(day.dayOfWeek)}</p>
          ) : null}
          {day.isAvailable ? (
            <div className="mt-2 space-y-2">
              {day.timeRanges.map((range, index) => (
                <div key={index} className="flex items-center gap-2">
                  <TimePicker
                    value={range.startTime}
                    onChange={(value) => updateRange(day.dayOfWeek, index, { startTime: value })}
                  />
                  <span className="text-xs text-muted-foreground">to</span>
                  <TimePicker
                    value={range.endTime}
                    onChange={(value) => updateRange(day.dayOfWeek, index, { endTime: value })}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    className="text-muted-foreground"
                    disabled={disabled || day.timeRanges.length === 1}
                    onClick={() => removeRange(day.dayOfWeek, index)}
                    aria-label="Remove range"
                  >
                    <Trash2 className="size-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <p className="mt-1 text-xs text-muted-foreground">Unavailable</p>
          )}
        </div>
      ))}
    </div>
  );
}
